import { createHash } from 'node:crypto';
import { platformImportResolutionCapability, platformImportResolutionProofSchema, } from '@crest/platform-contracts/import-resolution';
import { platformJsonValueSchema, } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const tasksNotificationPreferencesImportCapability = 'tasks.notification-preferences-import.v1';
export const tasksNotificationPreferencesImportManifestVersion = 'tasks-notification-preferences-source-manifest.v1';
export const tasksNotificationPreferencesImportMappingVersion = 'tasks-notification-preferences-normalized-v1';
export const tasksNotificationPreferencesImportEntityType = 'notification-preference';
const timestamp = z.string().regex(/^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d\.\d{6}Z$/);
const sourceId = z.string().trim().min(1).max(240);
const tenantKey = z.string().trim().min(1).max(120);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const uuid = z.string().uuid();
const localTime = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d\.\d{6}$/);
export const tasksNotificationPreferencesImportWriterFenceSchema = z.strictObject({
    fenceEpoch: z.string().trim().min(1).max(240),
    sourceWriterState: z.enum(['authoritative', 'frozen']),
    targetWriterState: z.literal('disabled'),
    noDualWriters: z.literal(true),
    sourceThrough: timestamp,
    attestationSha256: sha256,
    attestedAt: timestamp,
}).superRefine((value, context) => {
    if (Date.parse(value.attestedAt) < Date.parse(value.sourceThrough)) {
        context.addIssue({
            code: 'custom',
            path: ['attestedAt'],
            message: 'Writer attestation cannot predate sourceThrough.',
        });
    }
});
export const tasksNotificationPreferencesImportSourceManifestSchema = z.strictObject({
    manifestVersion: z.literal(tasksNotificationPreferencesImportManifestVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceRepository: z.string().trim().min(1).max(240),
    sourceSystemRevision: z.string().trim().min(1).max(240),
    sourceKind: z.enum(['synthetic', 'sanitized', 'restricted']),
    sourceCommitSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceTreeSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceSchemaSha256: sha256,
    captureMode: z.enum(['base', 'final-delta']),
    capturedAt: timestamp,
    writerFence: tasksNotificationPreferencesImportWriterFenceSchema,
    tables: z.array(z.strictObject({
        tableName: z.literal('task_notification_preferences'),
        rowCount: z.number().int().nonnegative().safe(),
        sha256,
    })).length(1),
}).superRefine((value, context) => {
    if (Date.parse(value.capturedAt) !== Date.parse(value.writerFence.sourceThrough)) {
        context.addIssue({
            code: 'custom',
            path: ['writerFence', 'sourceThrough'],
            message: 'Writer watermark must equal the manifest capture time.',
        });
    }
    if (value.captureMode === 'final-delta'
        && value.writerFence.sourceWriterState !== 'frozen') {
        context.addIssue({
            code: 'custom',
            path: ['writerFence', 'sourceWriterState'],
            message: 'Final delta requires the legacy source writer to be frozen.',
        });
    }
});
/** Exact business fields from public.task_notification_preferences. */
export const tasksNotificationPreferencesImportSourceSchema = z.strictObject({
    id: sourceId,
    tenant_id: uuid,
    user_id: uuid,
    assignment_email_enabled: z.boolean(),
    reminder_email_enabled: z.boolean(),
    reminder_frequency: z.enum(['off', 'daily', 'weekdays', 'hourly']),
    reminder_time_local: localTime,
    timezone: z.string().min(1).max(100),
    created_at: timestamp,
    updated_at: timestamp,
});
export const tasksNotificationPreferencesImportRowSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    sourceTable: z.literal('task_notification_preferences'),
    sourceTenantExternalKey: tenantKey,
    sourceId,
    sourceSha256: sha256,
    sourceUpdatedAt: timestamp,
    payload: tasksNotificationPreferencesImportSourceSchema,
}).superRefine((row, context) => {
    if (row.sourceId !== row.payload.id) {
        context.addIssue({
            code: 'custom',
            path: ['sourceId'],
            message: 'sourceId must equal payload.id.',
        });
    }
    if (row.sourceUpdatedAt !== row.payload.updated_at) {
        context.addIssue({
            code: 'custom',
            path: ['sourceUpdatedAt'],
            message: 'sourceUpdatedAt must equal payload.updated_at exactly.',
        });
    }
});
export const tasksNotificationPreferencesMembershipSourceMappingSchema = z.strictObject({
    sourceTenantExternalKey: tenantKey,
    sourceUserId: uuid,
    membershipSourceExternalKey: uuid,
});
export const tasksNotificationPreferencesImportSnapshotPayloadSchema = z.strictObject({
    contractVersion: z.literal(tasksNotificationPreferencesImportCapability),
    mappingVersion: z.literal(tasksNotificationPreferencesImportMappingVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceTenantId: uuid,
    tenantExternalKey: tenantKey,
    runKind: z.enum(['base', 'final-delta']),
    sourceEpoch: z.string().trim().min(1).max(240),
    parentSourceSha256: sha256.nullable(),
    sourceManifest: tasksNotificationPreferencesImportSourceManifestSchema,
    membershipSourceMappings: z.array(tasksNotificationPreferencesMembershipSourceMappingSchema).max(100_000),
    rows: z.array(z.unknown()).max(100_000),
}).superRefine((value, context) => {
    if ((value.runKind === 'base') !== (value.parentSourceSha256 === null)) {
        context.addIssue({
            code: 'custom',
            path: ['parentSourceSha256'],
            message: 'Base has no parent; final delta requires the exact parent checksum.',
        });
    }
    if (value.runKind !== value.sourceManifest.captureMode) {
        context.addIssue({
            code: 'custom',
            path: ['sourceManifest', 'captureMode'],
            message: 'Manifest mode must match run kind.',
        });
    }
    if (value.sourceManifest.tables[0]?.rowCount !== value.rows.length) {
        context.addIssue({
            code: 'custom',
            path: ['sourceManifest', 'tables'],
            message: 'Manifest row count must exactly reconcile to snapshot rows.',
        });
    }
});
export const tasksNotificationPreferencesImportSnapshotSchema = tasksNotificationPreferencesImportSnapshotPayloadSchema.extend({
    sourceManifestSha256: sha256,
    sourceSha256: sha256,
});
export const tasksNotificationPreferencesImportTargetPayloadSchema = z.strictObject({
    membershipId: uuid,
    assignmentEmailEnabled: z.boolean(),
    reminderEmailEnabled: z.boolean(),
    reminderFrequency: z.enum(['off', 'daily', 'weekdays', 'hourly']),
    reminderTimeLocal: localTime,
    timeZone: z.string().min(1).max(100),
    createdAt: timestamp,
    updatedAt: timestamp,
});
export const tasksNotificationPreferencesMembershipResolutionSchema = z.strictObject({
    sourceExternalKey: uuid,
    sourceUserId: uuid,
    sourceTenantExternalKey: tenantKey,
    resolution: platformImportResolutionProofSchema,
    verified: z.literal(true),
}).superRefine((value, context) => {
    if (value.resolution.capability !== platformImportResolutionCapability
        || value.resolution.entityType !== 'membership'
        || value.sourceExternalKey !== value.resolution.sourceId
        || value.sourceTenantExternalKey !== value.resolution.sourceTenantExternalKey) {
        context.addIssue({
            code: 'custom',
            path: ['resolution'],
            message: 'Membership resolution must exactly bind the source membership and tenant.',
        });
    }
});
export const tasksNotificationPreferencesImportRejectReasonSchema = z.enum([
    'schema-invalid',
    'cross-tenant',
    'duplicate-source-identity',
    'duplicate-target-membership',
    'source-after-capture',
    'unresolved-membership',
]);
export const tasksNotificationPreferencesImportRejectSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    entityType: z.literal(tasksNotificationPreferencesImportEntityType),
    sourceId,
    sourceSha256: sha256,
    reasonCode: tasksNotificationPreferencesImportRejectReasonSchema,
    fieldPaths: z.array(z.string().trim().min(1).max(240)).min(1).max(20),
});
export const tasksNotificationPreferencesImportPlanActionSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    entityType: z.literal(tasksNotificationPreferencesImportEntityType),
    sourceId,
    sourceSha256: sha256,
    sourceUpdatedAt: timestamp,
    targetId: uuid,
    identityStrategy: z.literal('resolved-platform-membership'),
    payload: tasksNotificationPreferencesImportTargetPayloadSchema,
    sourceEvidence: tasksNotificationPreferencesImportSourceSchema,
    membershipResolution: tasksNotificationPreferencesMembershipResolutionSchema,
}).superRefine((action, context) => {
    if (action.membershipResolution.sourceUserId !== action.sourceEvidence.user_id
        || action.payload.membershipId !== action.membershipResolution.resolution.targetId
        || action.targetId !== action.payload.membershipId) {
        context.addIssue({
            code: 'custom',
            path: ['membershipResolution'],
            message: 'Membership resolution and target must exactly bind source evidence.',
        });
    }
});
export const tasksNotificationPreferencesImportPlanSchema = z.strictObject({
    contractVersion: z.literal(tasksNotificationPreferencesImportCapability),
    manifestVersion: z.literal(tasksNotificationPreferencesImportManifestVersion),
    mappingVersion: z.literal(tasksNotificationPreferencesImportMappingVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceTenantId: uuid,
    sourceRepository: z.string().trim().min(1).max(240),
    sourceSystemRevision: z.string().trim().min(1).max(240),
    sourceKind: z.enum(['synthetic', 'sanitized', 'restricted']),
    sourceCommitSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceTreeSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceSchemaSha256: sha256,
    sourceManifestSha256: sha256,
    tenantExternalKey: tenantKey,
    runKind: z.enum(['base', 'final-delta']),
    sourceEpoch: z.string().trim().min(1).max(240),
    parentSourceSha256: sha256.nullable(),
    capturedAt: timestamp,
    writerFence: tasksNotificationPreferencesImportWriterFenceSchema,
    sourceSha256: sha256,
    planSha256: sha256,
    actions: z.array(tasksNotificationPreferencesImportPlanActionSchema),
    rejects: z.array(tasksNotificationPreferencesImportRejectSchema),
    counts: z.strictObject({
        sourceRows: z.number().int().nonnegative(),
        plannedRows: z.number().int().nonnegative(),
        rejectedRows: z.number().int().nonnegative(),
    }),
}).superRefine((value, context) => {
    if (value.counts.sourceRows !== value.actions.length + value.rejects.length
        || value.counts.plannedRows !== value.actions.length
        || value.counts.rejectedRows !== value.rejects.length) {
        context.addIssue({
            code: 'custom',
            path: ['counts'],
            message: 'Every source row must produce exactly one action or quarantine reject.',
        });
    }
});
export const tasksNotificationPreferencesImportExecutionReceiptSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    entityType: z.literal(tasksNotificationPreferencesImportEntityType),
    sourceId,
    sourceSha256: sha256,
    sourceUpdatedAt: timestamp,
    targetId: uuid,
    identityStrategy: z.literal('resolved-platform-membership'),
    outcome: z.enum(['applied', 'verified']),
    previousTargetSha256: sha256.nullable(),
    targetSha256: sha256,
    targetCanonical: tasksNotificationPreferencesImportTargetPayloadSchema,
    rollbackToken: platformJsonValueSchema,
});
export const tasksNotificationPreferencesImportRollbackReceiptSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    outcome: z.enum([
        'applied',
        'already-rolled-back',
        'rejected-conflict',
        'rejected-state',
    ]),
    detailCode: z.string().trim().min(3).max(120),
    targetSha256: sha256.nullable(),
});
export function canonicalTasksNotificationPreferencesImportJson(value) {
    if (value === null)
        return 'null';
    if (typeof value === 'string' || typeof value === 'boolean')
        return JSON.stringify(value);
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            throw new Error('Import JSON numbers must be finite.');
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return `[${value.map(canonicalTasksNotificationPreferencesImportJson).join(',')}]`;
    }
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonicalTasksNotificationPreferencesImportJson(value[key] ?? null)}`).join(',')}}`;
}
export function hashTasksNotificationPreferencesImportValue(value) {
    return createHash('sha256')
        .update(canonicalTasksNotificationPreferencesImportJson(value))
        .digest('hex');
}
//# sourceMappingURL=tasks-import.contracts.js.map